import React from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Form,
  FormGroup,
  Button
} from "reactstrap";

const CompetencyForm = ({
  competency,
  competencies,
  errors,
  pendingError,
  handleCompetencyChange,
  addCompetency,
  removeCompetency,
  clearPending,
  t
}) => {
  const hasInput = Boolean(competency.name || competency.customName || competency.level || competency.description);
  return (
    <Card className="mb-4" id="competency">
      <CardBody>
        <CardTitle className="h4">{t("Competencies")}</CardTitle>
        <p className="card-title-desc">
          {t("Pick a competency and its level, then tap \"Add\". Repeat for each one. Then save the curriculum.")}
        </p>
        {pendingError && (
          <div className="alert alert-warning py-2" role="alert">
            <i className="bx bx-error-circle me-1" />{pendingError}
          </div>
        )}

        <Form onSubmit={(e) => { e.preventDefault(); addCompetency(); }}>
          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="name">{t("Competency Name")}</Label>
                <Input
                  type="select"
                  id="name"
                  name="name"
                  value={competency.name}
                  onChange={handleCompetencyChange}
                  invalid={!!errors.name}
                >
                  <option value="">{t("Select competency")}</option>
                  <option value="english">{t("English")}</option>
                  <option value="spanish">{t("Spanish")}</option>
                  <option value="french">{t("French")}</option>
                  <option value="german">{t("German")}</option>
                  <option value="microsoft_office">{t("Microsoft Office")}</option>
                  <option value="digital_marketing">{t("Digital Marketing")}</option>
                  <option value="project_management">{t("Project Management")}</option>
                  <option value="data_analysis">{t("Data Analysis")}</option>
                  <option value="graphic_design">{t("Graphic Design")}</option>
                  <option value="programming">{t("Programming")}</option>
                  <option value="others">{t("Others")}</option>
                </Input>
                {errors.name && (
                  <div className="text-danger small mt-1">
                    {errors.name}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="level">{t("Proficiency Level")}</Label>
                <Input
                  type="select"
                  id="level"
                  name="level"
                  value={competency.level}
                  onChange={handleCompetencyChange}
                  invalid={!!errors.level}
                >
                  <option value="">{t("Select proficiency level")}</option>
                  <option value="basic">{t("Basic")}</option>
                  <option value="intermediate">{t("Intermediate")}</option>
                  <option value="advanced">{t("Advanced")}</option>
                  <option value="specialist">{t("Specialist")}</option>
                </Input>
                {errors.level && (
                  <div className="text-danger small mt-1">
                    {errors.level}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
          
          {competency.name === 'others' && (
            <FormGroup className="mb-3">
              <Label for="customName">{t("Custom Competency Name")}</Label>
              <Input
                type="text"
                id="customName"
                name="customName"
                value={competency.customName || ''}
                onChange={handleCompetencyChange}
                placeholder={t("Enter custom competency name")}
                invalid={!!errors.customName}
              />
              {errors.customName && (
                <div className="text-danger small mt-1">
                  {errors.customName}
                </div>
              )}
            </FormGroup>
          )}
          
          <FormGroup className="mb-3">
            <Label for="description">{t("Description")}</Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              value={competency.description}
              onChange={handleCompetencyChange}
              placeholder={t("Enter description for this competency")}
              rows={3}
            />
          </FormGroup>
          
          <div className="d-flex flex-wrap gap-2 mb-4">
            <Button color="primary" outline type="submit">
              <i className="bx bx-plus me-1" />{t("Add Competency")}
            </Button>
            {hasInput && (
              <Button color="link" className="text-muted" type="button" onClick={clearPending}>
                {t("Clear")}
              </Button>
            )}
          </div>
          
          {/* Added competencies — card rows work on any screen width */}
          {competencies.length > 0 ? (
            <div aria-live="polite">
              <h6 className="text-muted mb-2">{t("Added competencies")} ({competencies.length})</h6>
              {competencies.map(comp => {
                const label = comp.name === 'others' ? comp.customName : t(comp.name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
                return (
                  <div key={comp.id} className="list-card p-3 d-flex justify-content-between align-items-start gap-3">
                    <div className="flex-grow-1">
                      <div className="fw-semibold">
                        {label}{" "}
                        <span className="badge bg-primary-subtle text-primary fw-normal ms-1">{t(comp.level.charAt(0).toUpperCase() + comp.level.slice(1))}</span>
                      </div>
                      {comp.description && <div className="text-muted small mt-1">{comp.description}</div>}
                    </div>
                    <Button color="danger" outline size="sm" className="btn-icon-tap" onClick={() => removeCompetency(comp.id)}
                      aria-label={`${t("Remove")} ${label}`} title={t("Remove")}>
                      <i className="bx bx-trash" aria-hidden="true" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-muted">
              {t("No competencies added yet")}
            </div>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

CompetencyForm.propTypes = {
  competency: PropTypes.object.isRequired,
  competencies: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  pendingError: PropTypes.string,
  handleCompetencyChange: PropTypes.func.isRequired,
  addCompetency: PropTypes.func.isRequired,
  removeCompetency: PropTypes.func.isRequired,
  clearPending: PropTypes.func,
  t: PropTypes.func.isRequired
};

export default CompetencyForm;